import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";

export default function RedeemSerial() {
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [redeemed, setRedeemed] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!code.trim()) { toast.error("Enter a serial code"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/me/serials/redeem", { code });
            setRedeemed(data.product);
            setCode("");
            toast.success(`${data.product.name} added to your account`);
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout role="client">
            <div className="mb-8" data-testid="redeem-serial-page">
                <div className="label-mono mb-3">/ Redeem serial</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Redeem a serial code</h1>
                <p className="text-avx-muted mt-2 max-w-2xl">
                    Have a serial code? Enter it below to register the plugin in your account — you'll get its license and downloads instantly.
                </p>
            </div>

            <div className="max-w-xl">
                <div className="panel p-6">
                    <form onSubmit={submit} data-testid="redeem-serial-form">
                        <div className="flex items-center gap-2 mb-4 text-avx-blue">
                            <KeyRound className="w-5 h-5" />
                            <span className="label-mono">/ Serial code</span>
                        </div>
                        <Input
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="AVX-XXXX-XXXX-XXXX"
                            className="bg-avx-bg border-avx-line h-12 font-mono text-lg tracking-widest"
                            data-testid="redeem-serial-input"
                        />
                        <button type="submit" disabled={loading} data-testid="redeem-serial-submit"
                            className="btn-primary w-full mt-4 py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm disabled:opacity-60">
                            {loading ? "Redeeming…" : "Redeem code"}
                        </button>
                    </form>
                </div>

                {redeemed && (
                    <div className="panel p-6 mt-5 border-avx-success/40 bg-avx-success/5" data-testid="redeem-serial-success">
                        <div className="flex items-start gap-3">
                            <CheckCircle2 className="w-6 h-6 text-avx-success shrink-0" />
                            <div>
                                <div className="font-heading font-bold text-lg">{redeemed.name} registered!</div>
                                <p className="text-sm text-avx-muted mt-1">It's now available in your account.</p>
                                <div className="flex gap-3 mt-4">
                                    <Link to="/dashboard/plugins" className="btn-primary px-4 py-2 text-[10px] font-mono uppercase tracking-[0.18em] rounded-sm" data-testid="redeem-goto-plugins">
                                        My plugins
                                    </Link>
                                    <Link to="/dashboard/downloads" className="btn-secondary px-4 py-2 text-[10px] font-mono uppercase tracking-[0.18em] rounded-sm">
                                        Downloads
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
